import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import type { Hero } from '../game/data/heroes';
import { getTribeDefinition } from '../game/data/tribes';
import { useEffectiveReducedMotion } from './useReducedMotion';
import { EDGES } from '../theme/tokens';

// Hero lightbox — the canonical deep view of a hero, and the only place the
// full portrait page is shown.
//
// Portraits are 3:4 full-page drawings: a full-body figure on visible paper
// with margins and a flat tribe-coloured block behind them. Every in-game
// surface now renders the square BUST derivative instead, because at tile
// size the page put the face at roughly 20px and every hero read as "small
// person on coloured paper". That crop is right for a tile and throws away
// the drawing, so this is where the page gets shown whole.
//
// Hence `object-contain` rather than the `object-cover` CardDetail uses: this
// view exists specifically to not crop.
//
// Sits at the same z as the card lightbox — they are the same kind of thing
// and can never be open at once.
const DETAIL_Z = 70;

export function HeroDetail({ hero, onClose }: { hero: Hero; onClose: () => void }) {
  const reducedMotion = useEffectiveReducedMotion();
  const tribe = hero.tribe ? getTribeDefinition(hero.tribe) : undefined;
  const isCholo = hero.faction === 'cholo';
  const factionFont = isCholo ? 'font-cholo' : 'font-emo';
  const tribeColor = tribe
    ? `#${tribe.color.toString(16).padStart(6, '0')}`
    : '#8a7e6c';
  const isActivePower = hero.powerType.startsWith('active_');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <motion.div
      key="hero-detail"
      role="dialog"
      aria-modal="true"
      aria-label={`${hero.name} details`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.16 }}
      onClick={onClose}
      className="pt-safe pb-safe fixed inset-0 flex items-center justify-center overflow-y-auto bg-asphalt/85 p-md sm:p-lg"
      style={{ zIndex: DETAIL_Z }}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 14 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
        transition={{ duration: reducedMotion ? 0 : 0.2, ease: 'easeOut' }}
        className="my-auto flex w-full max-w-[860px] flex-col gap-md rounded-md border bg-stucco1 p-md sm:p-lg lg:flex-row lg:gap-lg"
        style={{ borderColor: EDGES.soft }}
      >
        {/* The page, whole. object-contain and a height cap rather than a
            fixed box: the drawings are not all the same height once the
            optimizer has trimmed stray marks off the top of some pages. */}
        <div className="flex shrink-0 flex-col items-center gap-sm lg:w-[340px]">
          {hero.portrait ? (
            <img
              src={`${import.meta.env.BASE_URL}${hero.portrait}`}
              alt={hero.name}
              className="max-h-[62vh] w-full rounded-md border object-contain lg:max-h-[70vh]"
              style={{ borderColor: 'rgba(255,138,61,0.25)' }}
            />
          ) : (
            /* No art yet — say so plainly rather than showing an empty frame.
               The procedural HeroCard stencil is the fallback in game. */
            <div
              className="flex aspect-[3/4] w-full items-center justify-center rounded-md border border-dashed"
              style={{ borderColor: EDGES.faint }}
            >
              <span className="font-data text-[11px] uppercase tracking-[0.25em] text-graphite">
                no art yet
              </span>
            </div>
          )}
        </div>

        {/* Text column */}
        <div className="flex min-w-0 flex-1 flex-col gap-sm">
          <div className="flex items-start justify-between gap-sm">
            <div className="min-w-0">
              <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker">
                hero · {isActivePower ? 'active' : 'passive'}
                {isActivePower && hero.cost !== undefined ? ` · ${hero.cost}g` : ''}
              </p>
              <h2
                className={`mt-[2px] break-words text-[26px] leading-tight sm:text-[30px] ${factionFont}`}
                style={{ color: tribeColor }}
              >
                {hero.name}
              </h2>
              <p className="mt-[2px] font-data text-[10px] uppercase tracking-[0.25em] text-graphite">
                {tribe ? tribe.name : 'no tribe'} · {hero.faction}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="min-h-[44px] shrink-0 rounded-sm border px-sm font-data text-[14px] leading-none text-graphite hover:bg-stucco2 hover:text-bone"
              style={{ borderColor: EDGES.faint }}
            >
              ✕
            </button>
          </div>

          <p className="text-[14px] leading-snug text-bone">{hero.powerDescription}</p>

          {/* The tribe this hero pulls toward. Both fields are authored in
              tribes.ts and, like the card lightbox's, were never rendered
              anywhere the player could read them. */}
          {tribe && (
            <div
              className="rounded-sm border p-sm"
              style={{ borderColor: EDGES.faint }}
            >
              <p
                className="font-data text-[10px] uppercase tracking-[0.3em]"
                style={{ color: tribeColor }}
              >
                {tribe.name} · {tribe.verb}
              </p>
              <p className="mt-[4px] text-[13px] leading-snug text-marker">
                {tribe.fantasy}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
